import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EmailIcon from '@mui/icons-material/Email';
import type { GridColDef } from '@mui/x-data-grid';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractErrorMessage } from '../../core/services/api';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { PageHeader } from '../../shared/components/PageHeader';
import { CreateUserDialog } from './CreateUserDialog';
import { formatDate } from '../../shared/utils/format';

interface DescopeUser {
  userId: string;
  email: string | null;
  name: string | null;
  status: string;
  verifiedEmail: boolean;
  createdTime: number | null;
  roles: string[];
  tenants: Record<string, string[]>;
  loginIds: string[];
}

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'default' | 'error'> = {
  enabled: 'success',
  invited: 'warning',
  disabled: 'error',
};

export function UsersPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(
    null,
  );

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ['descope-management', 'users', search, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '200', page: '0' });
      if (search) params.set('text', search);
      if (statusFilter) params.set('status', statusFilter);
      return api
        .get<{ users: DescopeUser[]; total: number }>(`/descope-management/users?${params}`)
        .then((r) => r.data);
    },
    staleTime: 30_000,
  });

  const users: (DescopeUser & { id: string })[] = (data?.users ?? []).map((u) => ({
    ...u,
    id: u.userId,
  }));

  const resendInvite = useMutation({
    mutationFn: (userId: string) =>
      api.post<{ data: { message: string } }>(`/descope-management/users/${userId}/resend-invite`),
    onSuccess: (res) => {
      setToast({ message: res.data.data.message ?? 'Invitation resent', severity: 'success' });
    },
    onError: (err) => {
      setToast({ message: extractErrorMessage(err), severity: 'error' });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['descope-management', 'users'] });
    },
  });

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
          {(p.row as DescopeUser).email ?? (p.row as DescopeUser).loginIds[0] ?? '—'}
          {(p.row as DescopeUser).name && (
            <>
              <br />
              <Typography variant="caption" color="text.secondary">
                {(p.row as DescopeUser).name}
              </Typography>
            </>
          )}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (p) => {
        const s = (p.row as DescopeUser).status;
        return (
          <Chip
            size="small"
            label={s}
            color={STATUS_COLORS[s] ?? 'default'}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'tenants',
      headerName: 'Tenants',
      width: 100,
      renderCell: (p) => {
        const count = Object.keys((p.row as DescopeUser).tenants).length;
        return count > 0 ? (
          <Chip size="small" label={count} />
        ) : (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        );
      },
    },
    {
      field: 'createdTime',
      headerName: 'Created',
      width: 160,
      valueFormatter: (v) => (v ? formatDate(new Date((v as number) * 1000).toISOString()) : '—'),
    },
    {
      field: '__actions',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      renderCell: (p) => {
        const user = p.row as DescopeUser;
        if (user.status !== 'invited') return null;
        return (
          <Tooltip title="Resend invitation">
            <span>
              <Button
                size="small"
                startIcon={<EmailIcon fontSize="small" />}
                onClick={() => resendInvite.mutate(user.userId)}
                disabled={resendInvite.isPending}
              >
                Resend
              </Button>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title="Users"
        description="Manage users and their tenant assignments."
        actions={
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreateOpen(true)}>
            Invite User
          </Button>
        }
      />

      <Stack direction="row" spacing={2} sx={{ px: 2, pb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 280 }}
        />
        <Select
          size="small"
          displayEmpty
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value="enabled">Enabled</MenuItem>
          <MenuItem value="disabled">Disabled</MenuItem>
          <MenuItem value="invited">Invited</MenuItem>
        </Select>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          {extractErrorMessage(error)}
        </Alert>
      )}

      <DataTable<DescopeUser & { id: string }>
        rows={users}
        columns={columns}
        loading={isFetching}
        rowCount={users.length}
        paginationMode="client"
        disableToolbar
      />

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
